import {
	findPageNumbersByClass,
	getPageNumber,
} from './dom-pages.js';
import { readHiddenValue } from './input-values.js';

export const STEP_STATE_CHANGE_EVENT = 'sd:configurator:step-state-change';

const RESULT_STEP_DEFINITIONS = {
	advice: {
		containerClassKey: 'adviceContainerClass',
		statusKey: 'sd_advice_status',
		loadingDataKey: 'sdConfigAdviceLoading',
		noResultsDataKey: 'sdConfigAdviceNoResults',
	},
	roi: {
		containerClassKey: 'roiContainerClass',
		statusKey: 'sd_roi_status',
		loadingDataKey: 'sdConfigRoiLoading',
		noResultsDataKey: 'sdConfigRoiNoResults',
	},
};

/**
* Responsibility:
* - Normalize positive integer-like values used by shared step-state helpers.
*
* Touches:
* - Used by form id, page number, and max-visited parsing in this module.
*
* Guarantees:
* - Returns a positive integer or `0` when the input is invalid.
*/
function toPositiveInteger(value) {
	const parsed = Number.parseInt(String(value || ''), 10);
	return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

/**
* Responsibility:
* - Resolve the current Gravity Forms id from a configurator form element.
*
* Touches:
* - Reads `form.dataset.formid` and `form.id`.
*
* Guarantees:
* - Returns a positive integer form id or `0` when unavailable.
*/
function resolveFormId(form) {
	if (!(form instanceof HTMLElement)) {
		return 0;
	}

	const dataFormId = toPositiveInteger(form.dataset.formid);
	if (dataFormId > 0) {
		return dataFormId;
	}

	const match = String(form.id || '').match(/^gform_(\d+)$/);
	return match ? toPositiveInteger(match[1]) : 0;
}

/**
* Responsibility:
* - Read form dataset flags used by async result-step state.
*
* Touches:
* - Reads `form.dataset[...]` using the provided dataset key.
*
* Guarantees:
* - Returns `true` only when the dataset value equals `'1'`.
*/
function readDatasetFlag(form, key) {
	if (!(form instanceof HTMLElement) || typeof key !== 'string' || key.trim() === '') {
		return false;
	}

	return String(form.dataset[key] || '') === '1';
}

/**
* Responsibility:
* - Resolve the rendered GF page number for one configured async result step.
*
* Touches:
* - Reads the configured container class from runtime config and scans form pages via `findPageNumbersByClass()`.
*
* Guarantees:
* - Returns the first matching positive page number or `0` when the result page is unavailable.
*/
function resolveResultStepPageNumber(form, config, containerClassKey) {
	const className = String(config?.[containerClassKey] || '').trim();
	if (!(form instanceof HTMLElement) || className === '') {
		return 0;
	}

	const pageNumbers = findPageNumbersByClass(form, className);
	return pageNumbers.length > 0 ? toPositiveInteger(pageNumbers[0]) : 0;
}

/**
* Responsibility:
* - Build a page-to-kind lookup for async result steps in the current configurator form.
*
* Touches:
* - Resolves configured advice and ROI result pages from runtime config and DOM.
*
* Guarantees:
* - Returns a `Map` keyed by page number with stable async result kind values.
*/
function buildResultStepLookup(form, config) {
	const lookup = new Map();

	Object.entries(RESULT_STEP_DEFINITIONS).forEach(([kind, definition]) => {
		const pageNumber = resolveResultStepPageNumber(form, config, definition.containerClassKey);
		if (pageNumber > 0) {
			lookup.set(pageNumber, kind);
		}
	});

	return lookup;
}

/**
* Responsibility:
* - Read persisted async result-step status flags from hidden fields and datasets.
*
* Touches:
* - Reads one hidden status field plus loading/no-results dataset flags for the requested step kind.
*
* Guarantees:
* - Returns stable `status/loading/noResults` shape for advice and ROI result steps.
*/
function resolveResultStepRuntimeState(form, config, resultKind) {
	const definition = RESULT_STEP_DEFINITIONS[resultKind];
	if (!definition) {
		return {
			status: '',
			loading: false,
			noResults: false,
		};
	}

	return {
		status: String(readHiddenValue(form, config?.hiddenFieldMap, definition.statusKey) || '').trim(),
		loading: readDatasetFlag(form, definition.loadingDataKey),
		noResults: readDatasetFlag(form, definition.noResultsDataKey),
	};
}

/**
* Responsibility:
* - Decide whether one async result step is truly complete.
*
* Touches:
* - Consumes shared status/loading/no-results runtime state only.
*
* Guarantees:
* - Returns true only for successful, non-loading, non-empty async results.
*/
function isResultStepComplete(runtimeState, successStatus) {
	return runtimeState.loading !== true
		&& runtimeState.status === successStatus
		&& runtimeState.noResults !== true;
}

/**
* Responsibility:
* - Decide whether one standard GF page should count as completed from canonical visited-page progress.
*
* Touches:
* - Consumes only the current page number and the remembered max-visited page number.
*
* Guarantees:
* - Returns `true` only after the user has progressed beyond the provided page.
*/
function isStandardStepComplete(pageNumber, maxVisitedPage) {
	const normalizedPageNumber = toPositiveInteger(pageNumber);
	const normalizedMaxVisitedPage = toPositiveInteger(maxVisitedPage);

	return normalizedPageNumber > 0 && normalizedMaxVisitedPage > normalizedPageNumber;
}

/**
* Responsibility:
* - Decide whether one visited async result step should surface an unresolved/error state.
*
* Touches:
* - Consumes shared visited/runtime status values only.
*
* Guarantees:
* - Returns true for visited stale/error/no-results steps that are not currently loading or complete.
*/
function isResultStepAttention(visited, runtimeState, statuses) {
	if (!visited || runtimeState.loading === true) {
		return false;
	}

	if (runtimeState.noResults === true) {
		return true;
	}

	const errorStatus = String(statuses?.error || 'error');
	const staleStatus = String(statuses?.stale || 'stale');

	return runtimeState.status === errorStatus || runtimeState.status === staleStatus;
}

/**
* Responsibility:
* - Resolve which rendered GF pages currently contain documented validation-error markers.
*
* Touches:
* - Reads Orbital validation selectors `.gfield_error` and `.gform_validation_errors` from the current form DOM.
*
* Guarantees:
* - Returns a set of page numbers that should surface sidebar attention state for validation failures.
*/
function resolveValidationErrorPageNumbers(form, currentPage) {
	if (!(form instanceof HTMLElement)) {
		return new Set();
	}

	const validationErrorPages = new Set();
	Array.from(form.querySelectorAll('.gfield_error')).forEach((fieldContainer) => {
		const pageElement = fieldContainer.closest('.gform_page');
		const pageNumber = getPageNumber(pageElement);
		if (pageNumber > 0) {
			validationErrorPages.add(pageNumber);
		}
	});

	const hasValidationSummary = form.querySelector('.gform_validation_errors') instanceof HTMLElement;
	const normalizedCurrentPage = toPositiveInteger(currentPage);
	if (hasValidationSummary && normalizedCurrentPage > 0) {
		validationErrorPages.add(normalizedCurrentPage);
	}

	return validationErrorPages;
}

/**
* Responsibility:
* - Resolve a coarse semantic state name for one rendered sidebar step.
*
* Touches:
* - Used by `collectStepStates()` after completion/attention booleans are derived.
*
* Guarantees:
* - Returns one of `locked`, `visited`, `current`, `complete`, or `attention`.
*/
function resolveStepStateName(current, visited, complete, attention) {
	if (attention) {
		return 'attention';
	}

	if (current) {
		return 'current';
	}

	if (complete) {
		return 'complete';
	}

	if (visited) {
		return 'visited';
	}

	return 'locked';
}

/**
* Responsibility:
* - Compute shared sidebar truth state for every configured configurator step.
*
* Touches:
* - Reads visited-page history, hidden async statuses, and loading/no-results dataset flags.
* - Resolves advice/ROI result pages from runtime config and current form DOM.
*
* Guarantees:
* - Returns deterministic per-step state objects with explicit `visited`, `complete`, and `attention` booleans.
*/
export function collectStepStates(form, config, steps, currentPage, maxVisitedPage) {
	const resultLookup = buildResultStepLookup(form, config);
	const safeCurrentPage = toPositiveInteger(currentPage);
	const safeMaxVisitedPage = toPositiveInteger(maxVisitedPage);
	const successStatus = String(config?.statuses?.success || 'success');
	const validationErrorPages = resolveValidationErrorPageNumbers(form, safeCurrentPage);

	return (Array.isArray(steps) ? steps : []).map((step) => {
		const pageNumber = toPositiveInteger(step?.pageNumber);
		const current = pageNumber > 0 && pageNumber === safeCurrentPage;
		const visited = pageNumber > 0 && (current || pageNumber <= safeMaxVisitedPage);
		const resultKind = resultLookup.get(pageNumber) || '';
		const runtimeState = resultKind !== ''
			? resolveResultStepRuntimeState(form, config, resultKind)
			: null;
		const complete = runtimeState
			? isResultStepComplete(runtimeState, successStatus)
			: isStandardStepComplete(pageNumber, safeMaxVisitedPage);
		const attention = runtimeState
			? isResultStepAttention(visited, runtimeState, config?.statuses)
			: false;
		const validationError = validationErrorPages.has(pageNumber);
		const resolvedAttention = attention || validationError;
		const resolvedComplete = resolvedAttention ? false : complete;

		return {
			...step,
			pageNumber,
			current,
			visited,
			complete: resolvedComplete,
			attention: resolvedAttention,
			validationError,
			resultKind,
			stateName: resolveStepStateName(current, visited, resolvedComplete, resolvedAttention),
		};
	});
}

/**
* Responsibility:
* - Notify listeners that shared configurator step truth changed outside of GF page lifecycle events.
*
* Touches:
* - Dispatches a bubbling `CustomEvent` from the current form element.
*
* Guarantees:
* - Includes `formId` and optional `reason` detail for downstream sidebar/icon refresh listeners.
*/
export function dispatchStepStateChange(form, reason = '') {
	if (!(form instanceof HTMLElement)) {
		return;
	}

	form.dispatchEvent(new CustomEvent(STEP_STATE_CHANGE_EVENT, {
		bubbles: true,
		detail: {
			formId: resolveFormId(form),
			reason: String(reason || '').trim(),
		},
	}));
}
